import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { PillButton } from "./PillButton";
import { cn } from "@/lib/utils";

type Variant = "hero" | "purple" | "teal" | "sunset";

const variantClass: Record<Variant, string> = {
  hero: "mkt-gradient-hero",
  purple: "mkt-gradient-purple",
  teal: "mkt-gradient-teal",
  sunset: "mkt-gradient-sunset",
};

export const FeatureSection = ({
  eyebrow,
  title,
  description,
  cta = "Start For Free",
  ctaHref = "/auth?mode=signup",
  variant = "hero",
  mockup,
  reverse = false,
  proofChip,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  cta?: string;
  ctaHref?: string;
  variant?: Variant;
  mockup: ReactNode;
  reverse?: boolean;
  proofChip?: string;
}) => {
  const navigate = useNavigate();
  return (
    <section className="container mx-auto px-3 lg:px-8 py-10 sm:py-14 lg:py-20">
      <div className="max-w-3xl mx-auto text-center mb-8 sm:mb-12 lg:mb-14">
        {eyebrow && (
          <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--mkt-primary))] mb-3">
            {eyebrow}
          </div>
        )}
        <h2 className="text-3xl sm:text-5xl lg:text-6xl tracking-tight text-foreground leading-tight">
          {title}
        </h2>
        <p className="mt-4 sm:mt-5 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto">{description}</p>
        {proofChip && (
          <div className="mt-4 sm:mt-5 inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full bg-[hsl(var(--mkt-primary)/0.10)] text-[hsl(var(--mkt-primary-deep))] text-[11px] sm:text-xs font-semibold leading-tight">
            {proofChip}
          </div>
        )}
        {/* Per-section CTA hidden on mobile — single final CTA panel handles conversion */}
        <div className="mt-6 sm:mt-7 hidden sm:flex justify-center">
          <PillButton variant="primary" size="lg" onClick={() => navigate(ctaHref)}>
            {cta}
          </PillButton>
        </div>
      </div>

      <div
        className={cn(
          "relative rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden mkt-grain p-4 sm:p-10 lg:p-16",
          variantClass[variant]
        )}
      >
        <div className="absolute -right-20 top-10 w-[400px] h-[400px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="relative">{mockup}</div>
      </div>
    </section>
  );
};
